import { Tooltip } from "antd";
import React, { useMemo } from "react";
import { convertUtcToLocal } from "../../../library/utils";
import { DATE_TIME_FORMATS } from "../../../utils/dateTimes";
import { AUDIT_LOG_ATTRIBUTION } from "./auditLog/constants";
import mapAuditLogEntry from "./auditLog/mapAuditLogEntry";

/**
 * A consent change in the conversation feed (`messageType: CONSENT_UPDATE`).
 *
 * These are audit events, not conversation, so they render as a centered
 * one-line system row rather than a bubble — "Otto nudges turned on · By
 * EngagedAI Support" — with the timestamp on hover. Figma node 27039-49871.
 *
 * The message's `content` is the same audit record the Audit log drawer shows
 * (it carries the matching `audit_log_id`), just snake_cased, so the wording
 * comes from the shared `mapAuditLogEntry` and the two surfaces cannot drift.
 */
const ConsentUpdateMessage = ({ message, userIdMap }) => {
  const item = useMemo(() => {
    // `content` is a JSON audit record, not prose. If it is not parseable there
    // is nothing to say, and dumping the raw string into the thread would be
    // worse than staying silent.
    try {
      const payload = JSON.parse(message?.content || "");
      if (!payload || typeof payload !== "object") return null;
      return { ...mapAuditLogEntry(payload), updatedById: payload.updated_by_id };
    } catch {
      return null;
    }
  }, [message?.content]);

  if (!item) return null;

  // The payload names the actor by id only; the message itself carries the
  // matching user record, with the thread's own id→name map as the fallback.
  const actorName =
    (+message?.user?.id === +item.updatedById ? message?.user?.fullName : null) ||
    userIdMap?.[+item.updatedById] ||
    item.updatedByName ||
    message?.user?.fullName;

  const attribution = AUDIT_LOG_ATTRIBUTION[item.updatedByType];
  // An unrecognised actor type still attributes the change if a name resolved;
  // "By" alone, or a bare unexplained name, would read as a bug.
  const meta = attribution
    ? [attribution.prefix, attribution.byName ? actorName : null].filter(Boolean).join(" ")
    : actorName && `By ${actorName}`;

  const timestamp = convertUtcToLocal(
    message?.dateReceived || message?.dateSent,
    DATE_TIME_FORMATS.MMM_D_YYYY_DOT_h_mm_A
  );

  return (
    <div className="flex w-full justify-center px-4">
      <Tooltip title={timestamp}>
        {/* Baseline-aligned so the 14px title and 12px meta share a text
            baseline rather than being centred against each other. */}
        <span className="flex flex-wrap items-baseline justify-center gap-1 text-center">
          <span className="text-sm font-normal leading-[22px] text-black-88">{item.title}</span>
          {meta && (
            <span className="text-xs font-normal leading-5 text-black-45">{`· ${meta}`}</span>
          )}
        </span>
      </Tooltip>
    </div>
  );
};

export default ConsentUpdateMessage;
